import React, { useState } from 'react';
import { clearLeaderboard, getLeaderboard, clearBlitzLeaderboard, getBlitzLeaderboard } from '../game/leaderboard';
import type { GameMode, LeaderboardEntry, BlitzLeaderboardEntry } from '../game/types';

interface LeaderboardModalProps {
  onClose: () => void;
  highlightRank?: number | null;
  highlightBlitzId?: string | null;
  initialTab?: GameMode;
}

export const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
  onClose,
  highlightRank,
  highlightBlitzId,
  initialTab = 'classic',
}) => {
  const [activeTab, setActiveTab] = useState<GameMode>(initialTab);
  const [classicEntries, setClassicEntries] = useState<LeaderboardEntry[]>(getLeaderboard());
  const [blitzEntries, setBlitzEntries] = useState<BlitzLeaderboardEntry[]>(getBlitzLeaderboard());

  const handleClear = () => {
    if (activeTab === 'classic') {
      clearLeaderboard();
      setClassicEntries([]);
    } else {
      clearBlitzLeaderboard();
      setBlitzEntries([]);
    }
  };

  const currentCount = activeTab === 'classic' ? classicEntries.length : blitzEntries.length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content leaderboard-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="leaderboard-header">
          <h2>Leaderboard</h2>
          <div className="tab-group" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'classic'}
              className={`tab-btn ${activeTab === 'classic' ? 'active' : ''}`}
              id="tab-classic"
              onClick={() => setActiveTab('classic')}
            >
              Classic
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'blitz'}
              className={`tab-btn ${activeTab === 'blitz' ? 'active' : ''}`}
              id="tab-blitz"
              onClick={() => setActiveTab('blitz')}
            >
              Blitz
            </button>
          </div>
        </div>

        <div className="leaderboard-list">
          {activeTab === 'classic' ? (
            classicEntries.length === 0 ? (
              <div className="empty-state">No records yet. Finish a Classic Mode game!</div>
            ) : (
              classicEntries.map((entry) => {
                const isCurrent = highlightRank !== null && highlightRank !== undefined && entry.rank === highlightRank;
                return (
                  <div key={entry.rank} className={`leaderboard-row ${isCurrent ? 'current-game-row' : ''}`}>
                    <span className="rank-badge">#{entry.rank}</span>
                    <span className="time-val">{entry.formattedTime}</span>
                    <span className="meta-val">
                      {entry.hintsUsed > 0 ? `${entry.hintsUsed} hints (+${entry.penaltySeconds}s)` : 'No hints'}
                    </span>
                    <span className="date-val">{entry.date}</span>
                  </div>
                );
              })
            )
          ) : (
            blitzEntries.length === 0 ? (
              <div className="empty-state">No records yet. Finish a Blitz Mode game!</div>
            ) : (
              blitzEntries.map((entry) => {
                const isCurrent = highlightBlitzId !== null && highlightBlitzId !== undefined && entry.id === highlightBlitzId;
                const isCleared = entry.endReason === 'cleared';
                return (
                  <div
                    key={entry.id}
                    className={`leaderboard-row ${isCleared ? 'cleared-row' : ''} ${isCurrent ? 'current-game-row' : ''}`}
                  >
                    <span className="rank-badge">#{entry.rank}</span>
                    <span className="time-val">{entry.setsFound} sets</span>
                    <span className="meta-val blitz-meta">
                      {isCleared ? (
                        <span className="blitz-status-tag tag-cleared">Cleared ({entry.formattedTimeLeft})</span>
                      ) : entry.endReason === 'timeout' ? (
                        <span className="blitz-status-tag tag-timeout">Time Up</span>
                      ) : (
                        <span className="blitz-status-tag tag-mistake">Mistake</span>
                      )}
                    </span>
                    <span className="date-val">{entry.date}</span>
                  </div>
                );
              })
            )
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" id="btn-close-leaderboard" onClick={onClose}>
            Close
          </button>
          {currentCount > 0 && (
            <button className="btn btn-danger" onClick={handleClear}>
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

